import { useEffect, useState } from 'react';
import { getInvoicesListFromApi } from '../../../../api/accounting';
import errorMessage from '../../../../helpers/errorMessage';
import { tKey } from '../../../../helpers/translate';
import { InvoiceList, InvoiceListItem, InvoiceListTypes } from '../../../../redux/accounting/definitions';

interface StateFilterData {
  [key: string]: string;
}

type UseInvoiceList = {
  invoices: InvoiceListItem[];
  loading: boolean;
  stateFilters: StateFilterData;
  errorListMessage: string | null;
  removeInvoices: (invoice_ids: number[]) => void;
  checkedInvoices: () => InvoiceListItem[];
  addCheckedInvoices: (checkedInvoices: InvoiceListItem | InvoiceListItem[]) => void;
};

const useInvoiceList = (type: InvoiceListTypes, accountingLoading: boolean): UseInvoiceList => {
  const [invoices, setInvoices] = useState<InvoiceListItem[]>([]);
  const [defaultCheckedInvoices, setDefaultCheckedInvoices] = useState<InvoiceListItem[]>([]);
  const [stateFilters, setStateFilters] = useState<StateFilterData>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [errorListMessage, setErrorListMessage] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setErrorListMessage(null);
    getInvoicesListFromApi(type)
      .then((data: InvoiceList) => {
        setInvoices(data.invoices);
        const state_filters: StateFilterData = {};
        data.filtered_states.forEach((state: string) => {
          state_filters[state] = tKey(state);
        });
        setStateFilters(state_filters);
        setDefaultCheckedInvoices(data.invoices.filter((item: InvoiceListItem) => !!item.checked));
      })
      .catch(error => {
        setErrorListMessage(errorMessage(error));
      })
      .finally(() => setLoading(false));
  }, [accountingLoading, type]);

  const removeInvoices = (invoice_ids: number[]) => {
    setInvoices(invoices.filter((invoice: InvoiceListItem) => !invoice_ids.includes(invoice.id)));
  };

  const checkedInvoices = (): InvoiceListItem[] => {
    return invoices.filter((item: InvoiceListItem) => !!item.checked);
  };

  const addCheckedInvoices = (checkedInvoices: InvoiceListItem | InvoiceListItem[]) => {
    if (Array.isArray(checkedInvoices)) {
      if (checkedInvoices.length === 0) {
        setDefaultCheckedInvoices([]);
        setInvoices(
          invoices.map((item: InvoiceListItem) => ({
            ...item,
            checked: false,
          })),
        );
        return;
      }
      setInvoices(
        invoices.map((item: InvoiceListItem) => ({
          ...item,
          checked:
            checkedInvoices.some((checkedItem: InvoiceListItem) => checkedItem.id === item.id) ||
            defaultCheckedInvoices.some((checkedItem: InvoiceListItem) => checkedItem.id === item.id),
        })),
      );
    }
  };

  return {
    invoices,
    loading,
    stateFilters,
    errorListMessage,
    checkedInvoices,
    addCheckedInvoices,
    removeInvoices,
  };
};

export default useInvoiceList;
